import { useEffect, useState } from 'react';

import { CookieAccessor } from '../../Customers/UserProfile/Components/CookiesConsent/CookieAccessor';
import { CookieNamesViewModel } from '../../Layouts/Partial/CookieNamesViewModel.csharp';
import { ApplicationInsights } from '../ApplicationInsights';
import { TrackingAccessor } from '../TrackingAcessor';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
type BaseProps<TProps> = TProps extends { href?: string; onClick?: (...args: any[]) => any } ? TProps : never;
declare let cookieNames: CookieNamesViewModel;

export type NavigationTrackerSource =
    | 'ActionHero'
    | 'ActionMenu'
    | 'CallToAction'
    | 'Footer'
    | 'HeaderButton'
    | 'Navigation'
    | 'ProductCard'
    | 'ProductPage'
    | 'RxBanner'
    | 'RxMenu';
export type NavigationTrackerProps<TProps> = {
    href?: BaseProps<TProps>['href'];
    onClick?: BaseProps<TProps>['onClick'];
    disableTracking?: boolean;
    source: NavigationTrackerSource;
};

type NavigationTrackerPropsInternal<TProps> = NavigationTrackerProps<TProps> & {
    children: (href: BaseProps<TProps>['href'], onClick: BaseProps<TProps>['onClick']) => JSX.Element;
};

export const NavigationTracker = <TProps>({ children, href, onClick, disableTracking, source }: NavigationTrackerPropsInternal<BaseProps<TProps>>) => {
    const [trackedHref, setTrackedHref] = useState(href);

    useEffect(() => {
        if (disableTracking || !href) {
            return;
        }

        const url = new URL(href, document.baseURI);
        url.searchParams.set('navigation_source', source);

        setTrackedHref(url.toString());
    }, []);

    const track = () => {
        if (disableTracking || !TrackingAccessor.isStatisticsEnabled) {
            return;
        }

        let bankIdExpiresIn: number | null = null;
        const bankIdExpiration = CookieAccessor.getCookie('bankIDExpiration');
        if (bankIdExpiration) {
            bankIdExpiresIn = Math.round((+new Date(bankIdExpiration) - +new Date()) / 1000 / 60);
        }

        const commonProperties = {
            customerId: CookieAccessor.getCookie('customerID'),
            bankIdSessionId: CookieAccessor.getCookie(cookieNames.bankId.sessionId),
            bankIdExpiresIn,
            source
        };

        if (href) {
            // This removes unnecessary parts of the url.
            const url = new URL(href, document.baseURI);
            url.hash = url.password = url.search = url.username = '';

            ApplicationInsights.TrackEvent('navigationNavigated', {
                ...commonProperties,
                link: url.toString()
            });
        } else {
            ApplicationInsights.TrackEvent('navigationClicked', {
                ...commonProperties
            });
        }
    };

    const trackedOnClick: BaseProps<TProps>['onClick'] = (...args) => {
        if (!disableTracking) {
            track();
        }

        if (onClick) {
            return onClick(...args);
        }
    };

    return children(trackedHref, trackedOnClick);
};
